import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCurrency } from '../contexts/CurrencyContext'
import { useNotification } from '../contexts/NotificationContext'
import { invoiceService } from '../services/invoiceService'
import type { Invoice, InvoiceFilters, InvoiceStats, PlanLimits } from '../types/invoice'
import { 
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArchiveBoxArrowDownIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline'
import CurrencyIcon from '../components/CurrencyIcon'
import ProfileDropdown from '../components/ProfileDropdown'
import AppHeader from '../components/AppHeader'
import InvoiceUploadModal from '../components/InvoiceUploadModal'
import UploadedInvoicesList from '../components/UploadedInvoicesList'

const InvoicesPage = () => {
  const { user } = useAuth()
  const { selectedCurrency } = useCurrency()
  const { showSuccess, showError, showConfirm } = useNotification()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [stats, setStats] = useState<InvoiceStats | null>(null)
  const [planLimits, setPlanLimits] = useState<PlanLimits | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloadingZip, setIsDownloadingZip] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<InvoiceFilters>({})
  
  // Upload functionality state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadRefreshTrigger, setUploadRefreshTrigger] = useState(0)
  const [activeTab, setActiveTab] = useState<'invoices' | 'uploaded'>('invoices')

  useEffect(() => {
    if (user) {
      loadInvoices()
      loadStats()
      loadPlanLimits()
    }
  }, [user])

  const loadInvoices = async () => {
    if (!user) return
    
    setIsLoading(true)
    const result = await invoiceService.getInvoices(user.id, filters)
    
    if (result.success && result.invoices) {
      setInvoices(result.invoices)
    } else {
      console.error('Failed to load invoices:', result.error)
    }
    setIsLoading(false)
  }

  const loadStats = async () => {
    if (!user) return
    
    const result = await invoiceService.getInvoiceStats(user.id)
    if (result.success && result.stats) {
      setStats(result.stats)
    }
  }

  const loadPlanLimits = async () => {
    if (!user) return
    
    const limits = await invoiceService.getPlanLimits(user.id, user.subscriptionStatus || 'freemium')
    setPlanLimits(limits)
  }

  const handleSearch = (term: string) => {
    setSearchTerm(term)
    setFilters({ ...filters, search: term })
  }

  const handleFilterChange = (newFilters: Partial<InvoiceFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)
  }

  const handleDeleteInvoice = async (invoiceId: string) => {
    const invoice = invoices.find(inv => inv.id === invoiceId)
    if (!invoice) return
    
    showConfirm(
      'Delete Invoice',
      `Are you sure you want to delete invoice ${invoice.invoiceNumber}? This action cannot be undone.`,
      async () => {
        const result = await invoiceService.deleteInvoice(invoiceId)
        if (result.success) {
          setInvoices(invoices.filter(inv => inv.id !== invoiceId))
          loadStats() // Refresh stats
          showSuccess(
            'Invoice Deleted',
            `Invoice ${invoice.invoiceNumber} has been successfully deleted.`
          )
        } else {
          showError(
            'Delete Failed',
            result.error || 'Failed to delete invoice. Please try again.'
          )
        }
      }
    )
  }

  const getStatusIcon = (status: Invoice['status']) => {
    switch (status) {
      case 'draft':
        return <PencilIcon className="w-4 h-4 text-gray-400" />
      case 'sent':
        return <ArrowUpIcon className="w-4 h-4 text-blue-400" />
      case 'viewed':
        return <EyeIcon className="w-4 h-4 text-purple-400" />
      case 'paid':
        return <CheckCircleIcon className="w-4 h-4 text-green-400" />
      case 'overdue':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-400" />
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4 text-gray-400" />
      default:
        return <ClockIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'draft':
        return 'text-gray-400 bg-gray-500/20'
      case 'sent':
        return 'text-blue-400 bg-blue-500/20'
      case 'viewed':
        return 'text-purple-400 bg-purple-500/20'
      case 'paid':
        return 'text-green-400 bg-green-500/20'
      case 'overdue':
        return 'text-red-400 bg-red-500/20'
      case 'cancelled':
        return 'text-gray-400 bg-gray-500/20'
      default:
        return 'text-gray-400 bg-gray-500/20'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency.code
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Apply search filter to invoices
  const filteredInvoices = invoices.filter(invoice => {
    if (!searchTerm) return true
    
    const searchLower = searchTerm.toLowerCase()
    return (
      invoice.title.toLowerCase().includes(searchLower) ||
      invoice.invoiceNumber.toLowerCase().includes(searchLower) ||
      invoice.client?.name.toLowerCase().includes(searchLower) ||
      invoice.client?.company?.toLowerCase().includes(searchLower)
    )
  })

  useEffect(() => {
    loadInvoices()
  }, [filters])

  const handleDownloadAllAsZip = async () => {
    if (!user) return
    
    if (invoices.length === 0) {
      showError('No Invoices', 'You have no invoices to download.')
      return
    }

    showConfirm(
      'Download All Invoices',
      `This will download all ${invoices.length} invoices as a ZIP file organized by year. This may take a few moments. Continue?`,
      async () => {
        setIsDownloadingZip(true)
        try {
          const result = await invoiceService.downloadAllInvoicesAsZip(user.id, user)
          
          if (result.success) {
            showSuccess(
              'Download Complete',
              `Successfully downloaded ${invoices.length} invoices as a ZIP file.`
            )
          } else {
            showError(
              'Download Failed',
              result.error || 'Failed to create ZIP file. Please try again.'
            )
          }
        } catch (error) {
          console.error('Error downloading invoices:', error)
          showError(
            'Download Failed',
            'An unexpected error occurred while creating the ZIP file.'
          )
        } finally {
          setIsDownloadingZip(false)
        }
      }
    )
  }

  const handleUploadSuccess = () => {
    setUploadRefreshTrigger(prev => prev + 1)
    loadPlanLimits() // Refresh plan limits to update upload count
  }

  if (!user) {
    return <div>Please log in to view invoices.</div>
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors font-medium">
          Dashboard
        </Link>
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                Invoices
              </h1>
              <p className="text-gray-400">
                Manage your invoices and track payments
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {planLimits && !planLimits.canCreateInvoice && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="glass-mockup rounded-xl p-4 border-orange-500/30 bg-orange-500/10"
                >
                  <div className="flex items-center space-x-2 text-orange-400">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      Monthly limit reached ({planLimits.currentMonthUsage}/{planLimits.maxInvoicesPerMonth})
                    </span>
                  </div>
                  <Link to="/upgrade" className="text-xs text-orange-300 hover:text-orange-200 underline">
                    Upgrade for unlimited invoices
                  </Link>
                </motion.div>
              )}
              
              <Link to="/invoices/new">
                <motion.button
                  className={`btn-primary flex items-center space-x-2 ${
                    planLimits && !planLimits.canCreateInvoice ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  whileHover={{ scale: planLimits?.canCreateInvoice ? 1.02 : 1 }}
                  whileTap={{ scale: planLimits?.canCreateInvoice ? 0.98 : 1 }}
                                     disabled={!!(planLimits && !planLimits.canCreateInvoice)}
                >
                  <PlusIcon className="w-5 h-5" />
                  <span>New Invoice</span>
                </motion.button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <div className="glass-mockup rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-blue-400" />
                </div>
                <span className="text-2xl font-bold text-white">{stats.totalInvoices}</span>
              </div>
              <h3 className="text-gray-400 font-medium">Total Invoices</h3>
              <p className="text-sm text-gray-500 mt-1">
                {stats.thisMonthInvoices} this month
              </p>
            </div>

            <div className="glass-mockup rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <CurrencyIcon className="w-6 h-6 text-green-400" />
                </div>
                <span className="text-2xl font-bold text-white">{formatCurrency(stats.totalAmount)}</span>
              </div>
              <h3 className="text-gray-400 font-medium">Total Revenue</h3>
              <p className="text-sm text-gray-500 mt-1">
                {formatCurrency(stats.thisMonthAmount)} this month
              </p>
            </div>

            <div className="glass-mockup rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-400" />
                </div>
                <span className="text-2xl font-bold text-white">{formatCurrency(stats.paidAmount)}</span>
              </div>
              <h3 className="text-gray-400 font-medium">Paid</h3>
              <p className="text-sm text-gray-500 mt-1">
                {stats.totalAmount > 0 ? Math.round((stats.paidAmount / stats.totalAmount) * 100) : 0}% of total
              </p>
            </div>

            <div className="glass-mockup rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
                </div>
                <span className="text-2xl font-bold text-white">{formatCurrency(stats.overdueAmount)}</span>
              </div>
              <h3 className="text-gray-400 font-medium">Overdue</h3>
              <p className="text-sm text-gray-500 mt-1">
                Requires attention
              </p>
            </div>
          </motion.div>
        )}

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <div className="glass-mockup rounded-xl p-2">
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  console.log('Switching to invoices tab')
                  setActiveTab('invoices')
                }}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'invoices'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>Active Invoices</span>
                </div>
              </button>
              <button
                onClick={() => {
                  console.log('Switching to uploaded tab')
                  setActiveTab('uploaded')
                }}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'uploaded'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <CloudArrowUpIcon className="w-5 h-5" />
                  <span>Uploaded Invoices</span>
                </div>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-mockup rounded-xl p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              />
            </div>
            
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
              >
                <FunnelIcon className="w-5 h-5" />
                <span>Filters</span>
              </motion.button>

              {activeTab === 'invoices' && invoices.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDownloadAllAsZip}
                  disabled={isDownloadingZip}
                  className="px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
                >
                  <ArchiveBoxArrowDownIcon className="w-5 h-5" />
                  <span>{isDownloadingZip ? 'Creating ZIP...' : 'Download All as ZIP'}</span>
                </motion.button>
              )}

              {activeTab === 'uploaded' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowUploadModal(true)}
                  disabled={planLimits ? !planLimits.canUploadInvoice : false}
                  className="px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
                >
                  <CloudArrowUpIcon className="w-5 h-5" />
                  <span>Upload Invoice</span>
                </motion.button>
              )}
              
              {activeTab === 'invoices' && planLimits?.canCreateInvoice && (
                <Link to="/invoices/new">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>New Invoice</span>
                  </motion.button>
                </Link>
              )}
            </div>
          </div>

          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 pt-6 border-t border-white/10"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Status
                    </label>
                    <select
                      value={filters.status?.[0] || ''}
                      onChange={(e) => handleFilterChange({ 
                        status: e.target.value ? [e.target.value as Invoice['status']] : undefined 
                      })}
                      className="input-field"
                    >
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="viewed">Viewed</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Payment Status
                    </label>
                    <select
                      value={filters.paymentStatus?.[0] || ''}
                      onChange={(e) => handleFilterChange({ 
                        paymentStatus: e.target.value ? [e.target.value as Invoice['paymentStatus']] : undefined 
                      })}
                      className="input-field"
                    >
                      <option value="">All Payment Statuses</option>
                      <option value="unpaid">Unpaid</option>
                      <option value="partial">Partial</option>
                      <option value="paid">Paid</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Date Range
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="date"
                        value={filters.dateFrom || ''}
                        onChange={(e) => handleFilterChange({ dateFrom: e.target.value })}
                        className="input-field flex-1 text-sm"
                      />
                      <input
                        type="date"
                        value={filters.dateTo || ''}
                        onChange={(e) => handleFilterChange({ dateTo: e.target.value })}
                        className="input-field flex-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Content Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-mockup rounded-xl overflow-hidden"
        >
          {activeTab === 'invoices' ? (
            // Regular Invoices Content
            isLoading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-400">Loading invoices...</p>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="p-8 text-center">
                <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No invoices found</h3>
                <p className="text-gray-400 mb-6">
                  {searchTerm || Object.keys(filters).length > 0 
                    ? 'Try adjusting your search or filters'
                    : 'Create your first invoice to get started'
                  }
                </p>
                {(!searchTerm && Object.keys(filters).length === 0) && (
                  <Link to="/invoices/new">
                    <button className="btn-primary">
                      Create Your First Invoice
                    </button>
                  </Link>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5 border-b border-white/10">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Invoice</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Client</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Amount</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Due Date</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {filteredInvoices.map((invoice, index) => (
                        <motion.tr
                          key={invoice.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="hover:bg-white/5 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-white">{invoice.invoiceNumber}</div>
                              <div className="text-sm text-gray-400">{invoice.title}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-white">{invoice.client?.name}</div>
                              {invoice.client?.company && (
                                <div className="text-sm text-gray-400">{invoice.client.company}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-white">{formatCurrency(invoice.totalAmount)}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
                              {getStatusIcon(invoice.status)}
                              <span className="capitalize">{invoice.status}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-white">{formatDate(invoice.dueDate)}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <Link to={`/invoices/${invoice.id}`}>
                                <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                                  <EyeIcon className="w-4 h-4" />
                                </button>
                              </Link>
                              <Link to={`/invoices/${invoice.id}/edit`}>
                                <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                              </Link>
                              <button
                                onClick={() => handleDeleteInvoice(invoice.id)}
                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4">
                  {filteredInvoices.map((invoice, index) => (
                    <motion.div
                      key={invoice.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-white">{invoice.invoiceNumber}</h3>
                            <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                              {getStatusIcon(invoice.status)}
                              <span className="capitalize">{invoice.status}</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-400 mb-1">{invoice.title}</p>
                          <p className="text-sm text-gray-300">{invoice.client?.name}</p>
                          {invoice.client?.company && (
                            <p className="text-xs text-gray-400">{invoice.client.company}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-white">{formatCurrency(invoice.totalAmount)}</div>
                          <div className="text-xs text-gray-400">Due {formatDate(invoice.dueDate)}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-3 border-t border-white/10">
                        <div className="text-xs text-gray-400">
                          Created {formatDate(invoice.createdAt)}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Link to={`/invoices/${invoice.id}`}>
                            <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                              <EyeIcon className="w-4 h-4" />
                            </button>
                          </Link>
                          <Link to={`/invoices/${invoice.id}/edit`}>
                            <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          </Link>
                          <button
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            )
          ) : (
            // Uploaded Invoices Content
            <div className="p-6">
              <UploadedInvoicesList refreshTrigger={uploadRefreshTrigger} />
            </div>
          )}
        </motion.div>

        {/* Upload Modal */}
        <InvoiceUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUploadSuccess={handleUploadSuccess}
          planLimits={planLimits}
        />
      </main>
    </div>
  )
}

export default InvoicesPage 